const formatPostalCode = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

const formatMoney = (price) => {
  const value = Number(price);
  if (!Number.isFinite(value) || value <= 0) return 'Grátis';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDeadline = (rate) => {
  const days = rate.delivery_days;
  if (!Array.isArray(days) || !days.length) return '';
  const min = Number(days[0]);
  const max = Number(days[1] || days[0]);
  if (!min) return '';
  if (max && max !== min) return `${min} a ${max} dias úteis`;
  return min > 1 ? `${min} dias úteis` : `${min} dia útil`;
};

const cartRequest = (url, body) =>
  fetch(url, {
    method: body ? 'POST' : 'GET',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = data.description || data.message || data.error || 'Não foi possível calcular o frete.';
      throw new Error(typeof message === 'string' ? message : 'Não foi possível calcular o frete.');
    }
    return data;
  });

const getVariantId = (calculator) => {
  const root = calculator.closest('.product-details') || document;
  return root.querySelector('input[name="id"], select[name="id"]')?.value;
};

const getQuantity = (calculator) => {
  const root = calculator.closest('.product-details') || document;
  const value = Number(root.querySelector('input[name="quantity"]')?.value || 1);
  return Number.isFinite(value) && value > 0 ? value : 1;
};

const lookupAddress = async (postalCode) => {
  const response = await fetch(`https://viacep.com.br/ws/${postalCode}/json/`);
  if (!response.ok) throw new Error('Não foi possível localizar este CEP.');
  const data = await response.json();
  if (data.erro) throw new Error('CEP não encontrado.');
  return data;
};

const formatFullAddress = (address, postalCode) => {
  if (!address) return '';
  const street = [address.logradouro, address.complemento].filter(Boolean).join(', ');
  const neighborhood = address.bairro || '';
  const cityUf = [address.localidade, address.uf].filter(Boolean).join('/');
  const cep = postalCode?.length === 8 ? formatPostalCode(postalCode) : address.cep || '';
  const parts = [street, neighborhood, cityUf].filter(Boolean);
  if (!parts.length) return cep ? `CEP ${cep}` : '';
  return cep ? `${parts.join(' — ')} · CEP ${cep}` : parts.join(' — ');
};

const fetchRates = async (postalCode, address) => {
  const ratesUrl = new URL('/cart/shipping_rates.json', window.location.origin);
  ratesUrl.searchParams.set('shipping_address[zip]', postalCode);
  ratesUrl.searchParams.set('shipping_address[country]', 'Brazil');
  if (address?.uf) ratesUrl.searchParams.set('shipping_address[province]', address.uf);
  if (address?.localidade) ratesUrl.searchParams.set('shipping_address[city]', address.localidade);

  for (let attempt = 0; attempt < 6; attempt += 1) {
    const response = await fetch(ratesUrl, { headers: { Accept: 'application/json' } });
    const data = await response.json().catch(() => ({}));
    if (Array.isArray(data.shipping_rates)) return data.shipping_rates;
    if (response.status === 422 && data.zip) throw new Error('CEP inválido para entrega.');
    await new Promise((resolve) => setTimeout(resolve, 450));
  }

  return [];
};

const restoreCart = async (items) => {
  await cartRequest('/cart/clear.js', {});
  if (!items.length) return;

  await cartRequest('/cart/add.js', {
    items: items.map((item) => ({
      id: item.variant_id,
      quantity: item.quantity,
      ...(item.properties && Object.keys(item.properties).length ? { properties: item.properties } : {}),
    })),
  });
};

const renderRates = (list, rates) => {
  list.replaceChildren();
  rates.forEach((rate) => {
    const item = document.createElement('li');
    const name = document.createElement('span');
    const price = document.createElement('strong');
    const deadline = formatDeadline(rate);

    name.textContent = deadline ? `${rate.name} · ${deadline}` : rate.name;
    price.textContent = formatMoney(rate.price);
    item.append(name, price);
    list.append(item);
  });
  list.hidden = false;
};

for (const calculator of document.querySelectorAll('[data-souleve-shipping-calculator]')) {
  if (calculator.dataset.souleveShippingReady === 'true') continue;
  calculator.dataset.souleveShippingReady = 'true';

  const form = calculator.querySelector('form');
  const input = calculator.querySelector('input');
  const status = calculator.querySelector('[data-souleve-shipping-status]');
  const ratesList = calculator.querySelector('[data-souleve-shipping-rates]');
  const button = calculator.querySelector('button');

  if (!form || !input || !status) continue;

  let addressLine = calculator.querySelector('[data-souleve-shipping-address]');
  if (!addressLine) {
    addressLine = document.createElement('p');
    addressLine.className = 'souleve-shipping-calculator__address';
    addressLine.setAttribute('data-souleve-shipping-address', '');
    addressLine.hidden = true;
    form.insertAdjacentElement('afterend', addressLine);
  }

  let debounceTimer = null;
  let requestId = 0;
  let lastCalculatedCep = '';
  let loadingTimer = null;

  const stopLoading = () => {
    if (loadingTimer) {
      window.clearInterval(loadingTimer);
      loadingTimer = null;
    }
    status.classList.remove('is-loading');
  };

  const startLoading = (label) => {
    stopLoading();
    status.hidden = false;
    status.classList.add('is-loading');
    let step = 0;
    const render = () => {
      status.textContent = `${label}${'.'.repeat((step % 3) + 1)}`;
      step += 1;
    };
    render();
    loadingTimer = window.setInterval(render, 380);
  };

  const setStatus = (text, { loading = false } = {}) => {
    stopLoading();
    if (loading) {
      startLoading(text);
      return;
    }
    status.textContent = text;
    status.hidden = !text;
  };

  const showAddress = (text) => {
    if (!text) {
      addressLine.hidden = true;
      addressLine.textContent = '';
      input.removeAttribute('title');
      return;
    }
    addressLine.textContent = text;
    addressLine.hidden = false;
    input.setAttribute('title', text);
  };

  const clearResults = () => {
    setStatus('');
    showAddress('');
    if (ratesList) {
      ratesList.replaceChildren();
      ratesList.hidden = true;
    }
  };

  const calculateShipping = async ({ fromSubmit = false } = {}) => {
    const postalCode = input.value.replace(/\D/g, '');

    if (postalCode.length !== 8) {
      if (fromSubmit) {
        setStatus('Informe um CEP válido.');
        showAddress('');
        if (ratesList) ratesList.hidden = true;
        input.focus();
      } else {
        clearResults();
      }
      lastCalculatedCep = '';
      return;
    }

    if (!fromSubmit && postalCode === lastCalculatedCep) return;

    const variantId = getVariantId(calculator);
    if (!variantId) {
      setStatus('Selecione uma opção do produto para calcular o frete.');
      return;
    }

    const currentRequest = ++requestId;
    lastCalculatedCep = postalCode;
    setStatus('Buscando CEP', { loading: true });
    if (ratesList) ratesList.hidden = true;
    if (button) button.disabled = true;

    let snapshot = [];
    let cartChanged = false;

    try {
      const address = await lookupAddress(postalCode);
      if (currentRequest !== requestId) return;

      const fullAddress = formatFullAddress(address, postalCode);
      showAddress(fullAddress);
      setStatus('Calculando frete', { loading: true });

      const cart = await cartRequest('/cart.js');
      if (currentRequest !== requestId) return;

      snapshot = cart.items || [];
      cartChanged = true;

      await cartRequest('/cart/clear.js', {});
      await cartRequest('/cart/add.js', {
        items: [{ id: Number(variantId), quantity: getQuantity(calculator) }],
      });
      if (currentRequest !== requestId) return;

      const rates = await fetchRates(postalCode, address);
      if (currentRequest !== requestId) return;

      await restoreCart(snapshot);
      cartChanged = false;

      if (!rates.length) {
        setStatus('Não encontramos entrega para este CEP.');
        return;
      }

      setStatus(fullAddress ? 'Entrega para este endereço' : 'Opções de entrega');
      if (ratesList) renderRates(ratesList, rates);
    } catch (error) {
      if (currentRequest !== requestId) return;
      setStatus(error.message || 'Não foi possível calcular o frete agora.');
      showAddress('');
      if (ratesList) ratesList.hidden = true;
      lastCalculatedCep = '';
    } finally {
      if (cartChanged) {
        try {
          await restoreCart(snapshot);
        } catch {
          if (currentRequest === requestId) {
            setStatus('Não foi possível calcular o frete. Atualize a página e tente de novo.');
          }
        }
      }
      if (currentRequest === requestId && button) button.disabled = false;
    }
  };

  input.addEventListener('input', () => {
    input.value = formatPostalCode(input.value);
    const postalCode = input.value.replace(/\D/g, '');

    window.clearTimeout(debounceTimer);

    if (postalCode.length !== 8) {
      lastCalculatedCep = '';
      clearResults();
      return;
    }

    debounceTimer = window.setTimeout(() => {
      calculateShipping();
    }, 400);
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    window.clearTimeout(debounceTimer);
    calculateShipping({ fromSubmit: true });
  });
}
